import { useEffect, useState } from 'react';

import { IconDefinition, IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';

const iconDefinitionCache = new Map<string, IconDefinition>();

export type IconProps = Omit<FontAwesomeIconProps, 'icon'> & {
  icon: IconName;
};

/**
 * @see https://fontawesome.com/search?o=r&m=free
 */
export default function Icon({ icon, size = 'sm', ...props }: IconProps) {
  const iconDefinition = useIconDefinition(icon);

  if (!iconDefinition) {
    return null;
  }

  return <FontAwesomeIcon icon={iconDefinition} size={size} {...props} />;
}

async function getIconDefinitionByName(name: IconName) {
  if (iconDefinitionCache.has(name)) {
    return iconDefinitionCache.get(name);
  }

  const moduleName = `fa${name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')}`;

  try {
    let iconDefinition: IconDefinition | undefined;

    const solidIcons = await import('@fortawesome/free-solid-svg-icons');
    iconDefinition = solidIcons[moduleName as keyof typeof solidIcons] as IconDefinition;

    if (!iconDefinition) {
      const brandsIcons = await import('@fortawesome/free-brands-svg-icons');
      iconDefinition = brandsIcons[moduleName as keyof typeof brandsIcons] as IconDefinition;
    }

    iconDefinitionCache.set(name, iconDefinition);

    return iconDefinition;
  } catch (err) {
    console.error(err);
    return Promise.resolve(undefined);
  }
}

function useIconDefinition(name: IconName) {
  const [iconDefinition, setIconDefinition] = useState<IconDefinition | undefined>(iconDefinitionCache.get(name));

  useEffect(() => {
    getIconDefinitionByName(name).then((iconDefinition) => {
      if (!iconDefinition) {
        return;
      }

      setIconDefinition(iconDefinition);
    });
  }, [name]);

  return iconDefinition;
}
